import { useState } from 'react'
import { useSlideUpDialog } from '../../cart/useSlideUpDialog'
import { useCart } from '../../cart/useCart'
import { priceStringToCents } from '../../cart/money'
import type { MenuItem } from '../../api/types'
import { formatZAR, formatZARCents } from '../../lib/format'

interface Props {
  item: MenuItem | null
  onClose: () => void
}

interface AddToOrderProps {
  item: MenuItem
  onAdd: (quantity: number) => void
}

// Owns quantity state for a single item. Keyed by item.id in ItemSheet below
// so switching items resets quantity to 1 via remount, rather than via a
// setState-in-effect (which react-hooks/set-state-in-effect flags as an
// anti-pattern — see https://react.dev/learn/you-might-not-need-an-effect).
function AddToOrderControls({ item, onAdd }: AddToOrderProps) {
  const [quantity, setQuantity] = useState(1)
  const unitCents = priceStringToCents(item.price)

  return (
    <>
      <div className="flex items-center gap-4">
        <button
          onClick={() => setQuantity((q) => Math.max(1, q - 1))}
          aria-label="Decrease quantity"
          className="w-9 h-9 rounded-full border border-neutral-300 flex items-center justify-center text-lg font-bold"
        >
          −
        </button>
        <span className="text-base font-semibold w-6 text-center">{quantity}</span>
        <button
          onClick={() => setQuantity((q) => q + 1)}
          aria-label="Increase quantity"
          className="w-9 h-9 rounded-full border border-neutral-300 flex items-center justify-center text-lg font-bold"
        >
          +
        </button>
      </div>
      <button
        onClick={() => onAdd(quantity)}
        className="w-full rounded-[var(--chip-radius)] bg-brand-accent text-[var(--brand-accent-text)] py-3 font-semibold"
      >
        Add to order — {formatZARCents(unitCents * quantity)}
      </button>
      {/* CartBar's own aria-live region goes inert while this dialog is open
          (showModal() makes everything outside it inert per the HTML spec) —
          mirror the stepper's running total here so it's still announced. */}
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        Quantity {quantity}, {formatZARCents(unitCents * quantity)}
      </span>
    </>
  )
}

export function ItemSheet({ item, onClose }: Props) {
  const { dialogRef, closeBtnRef, requestClose, handleCancel, handleDialogClick } =
    useSlideUpDialog(item, onClose)
  const { addItem } = useCart()

  if (!item) return null

  function handleAddToOrder(quantity: number) {
    if (!item) return
    addItem(
      {
        menuItemId: item.id,
        name: item.name,
        unitPriceCents: priceStringToCents(item.price),
        imageThumbnail: item.image_thumbnail,
      },
      quantity,
    )
    requestClose()
  }

  return (
    <dialog
      ref={dialogRef}
      onClick={handleDialogClick}
      onCancel={handleCancel}
      className={[
        'w-full max-w-lg rounded-t-[var(--sheet-radius)] sm:rounded-[var(--sheet-radius)] p-0 m-0 mt-auto sm:m-auto',
        'bg-white text-neutral-900',
        'shadow-xl backdrop:bg-black/50 backdrop:backdrop-blur-sm',
        'max-h-[90dvh] overflow-y-auto',
      ].join(' ')}
      aria-modal="true"
      aria-label={item.name}
    >
      {/* Full image */}
      <div className="w-full aspect-video bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
        {item.image ? (
          <img
            src={item.image}
            alt={item.name}
            className="w-full h-full object-cover"
            loading="eager"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center" aria-hidden="true">
            <svg
              className="w-16 h-16 text-neutral-300 dark:text-neutral-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3 20.25h18M3.75 3h16.5A.75.75 0 0121 3.75v12a.75.75 0 01-.75.75H3.75A.75.75 0 013 15.75v-12A.75.75 0 013.75 3z"
              />
            </svg>
          </div>
        )}
      </div>

      <div className="p-5 flex flex-col gap-3">
        {/* Drag handle (mobile affordance) */}
        <div className="sm:hidden w-10 h-1 rounded-full bg-neutral-300 dark:bg-neutral-600 mx-auto -mt-2 mb-1" aria-hidden="true" />

        <div className="flex items-start justify-between gap-3">
          <h2 className="text-xl font-bold leading-snug">{item.name}</h2>
          <button
            ref={closeBtnRef}
            onClick={requestClose}
            aria-label="Close"
            className="shrink-0 rounded-full w-8 h-8 flex items-center justify-center text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {item.description && (
          <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
            {item.description}
          </p>
        )}

        <p className="text-lg font-bold">{formatZAR(item.price)}</p>

        {item.is_available && (
          <AddToOrderControls key={item.id} item={item} onAdd={handleAddToOrder} />
        )}
      </div>
    </dialog>
  )
}
